import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonFloating from 'assets/components/button/themes/Theme.Button.Floating';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import DataMap from 'assets/components/dataMap/DataMap';
import SortButton from 'assets/components/filters/sortButton/SortButton';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ProgressWindow from 'assets/components/progressWindow/ProgressWindow';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import useApi from 'assets/hooks/api/useApi';
import { Roles } from 'assets/hooks/authInfo/useAuthInfo';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import { defaultDateFormat, defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { cleanValues } from 'assets/utils/data/Object';
import { combineStrings, replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { isEmpty, merge, sortBy } from 'lodash';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import VertaforeStatuses from 'models/enums/VertaforeDocumentStatuses';
import contractStatusFlowApi from 'models/sales/contract/statusFlow/Api.Contract.StatusFlow';
import React, { useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { SalesNewContractAction } from '../../newContract/Sales.NewContract.Hooks';
import { salesNewContractIndexRoute } from '../../newContract/Sales.NewContract.Index';
import { SalesContractAction, useSalesContractProvider } from '../Sales.Contract.Hooks';
import ContractStatusStateMachine from '../other/Contract.StatusStateMachine';
import SalesContractActions from './Sales.Contract.Actions';
import SalesContractFilters from './Sales.Contract.Filters';
import ContractStatus from 'app/components/ContractStatus';

export default function ContractList() {
  const { lang } = getLocales();
  const [newCreator, setNewCreator] = useState<{ contract: Model.IContract; user: Model.IUser }>(undefined);
  const [isLoadingSelectAll, setIsLoadingSelectAll] = useState<boolean>(undefined);
  const { payload: statusInfo } = useApi({ action: contractStatusFlowApi });
  const {
    listApi,
    pageRouter,
    permissions,
    userListApi,
    changeContractCreator,
    isStatusChanging,
    selectedContracts,
    setSelectedContracts,
    isContractEligibleForSelection,
    getAllSelectedContracts,
  } = useSalesContractProvider();
  const { redirect: redirectToNewContract } = usePageRouter<
    Module.Sales.NewContract.Params,
    Module.Sales.NewContract.Query
  >({ route: salesNewContractIndexRoute });

  function handleSort(newFilters: Api.IDefaultListRequest) {
    pageRouter.updateQuery({
      ...pageRouter.query,
      list: cleanValues({
        ...pageRouter.query?.list,
        ...newFilters,
      }),
    });
  }

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.contract}
      render={lang.noInfo}
    />
  ) : (
    <>
      <ProgressWindow />
      <Page
        class={ThemePageList}
        isLoading={listApi.isExecuting || isStatusChanging}
        header={
          <>
            <h2 style={{ marginRight: 'auto' }} children={lang.contract} />
            {isMobile && <SalesContractFilters />}
            <Box class={ThemeWrapperStack}>
              <SalesContractActions key="actions" />
              <Button
                class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
                active
                media={<Icon class="fas-plus" />}
                render={!isMobile && lang.addNew}
                onClick={redirectToNewContract}
                disabled={!permissions.CREATE_CONTRACT}
                data={{ action: SalesNewContractAction.VEHICLE }}
              />
            </Box>
          </>
        }
        subHeader={isDesktop && <SalesContractFilters />}
        render={
          <>
            {newCreator && (
              <Confirm
                itemName={`${combineStrings(' ', newCreator.user.firstName, newCreator.user.lastName)}`}
                info={
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>
                      {lang.contractNumber}: {newCreator.contract.appNumber}
                    </span>
                    <span style={{ fontWeight: 700, color: 'yellow' }}>{lang.changeContractCreatorWarning}</span>
                  </div>
                }
                actionName={`${lang.changeContractCreator}: `}
                onClose={() => setNewCreator(undefined)}
                onConfirm={async ({ contract, user }) => {
                  await changeContractCreator({ contractId: contract.id, creatorId: user?.id });
                  setNewCreator(undefined);
                }}
                data={newCreator}
                subcomponents={ThemeConfirmDefault}
                confirmationType="nextButton"
              />
            )}
            <table className={ThemeTableSimple}>
              <thead>
                <tr>
                  <th>
                    <Button
                      class={{ button: ThemeButtonCircle }}
                      media={
                        selectedContracts?.length === listApi.payload?.count
                          ? 'fas-check-square'
                          : selectedContracts?.length === 0
                            ? 'far-square'
                            : 'fas-minus-square'
                      }
                      disabled={!listApi?.payload?.count || !permissions.CREATE_STATEMENTS_FROM_CONTRACT_LIST}
                      onClick={async () => {
                        setIsLoadingSelectAll(true);
                        setSelectedContracts(await getAllSelectedContracts());
                        setIsLoadingSelectAll(undefined);
                      }}
                      isLoading={isLoadingSelectAll}
                      active={!isEmpty(selectedContracts) ? true : false}
                      htmlElementProps={{ title: lang.selectAll }}
                    />
                  </th>
                  <th>
                    {lang.number}
                    <SortButton
                      orderBy="appNumber"
                      thenBy={['client.FirstName', 'client.LastName', 'vehicle.Vin']}
                      apiBody={listApi.body}
                      onSort={handleSort}
                    />
                  </th>
                  <th>{lang.status}</th>
                  <th>
                    {lang.product}
                    <SortButton orderBy="product.name" apiBody={listApi.body} onSort={handleSort} />
                  </th>
                  <th>
                    {lang.vehicle}/{lang.vin}
                    <SortButton
                      orderBy="vehicle.makeCode"
                      thenBy="vehicle.model"
                      apiBody={listApi.body}
                      onSort={handleSort}
                    />
                  </th>
                  <th>
                    {lang.client}
                    <SortButton
                      orderBy="client.firstName"
                      thenBy="client.lastName"
                      apiBody={listApi.body}
                      onSort={handleSort}
                    />
                  </th>
                  <th>
                    {lang.dealer}
                    <SortButton orderBy="dealer.name" apiBody={listApi.body} onSort={handleSort} />
                  </th>
                  <th>
                    {lang.createdBy}
                    <SortButton
                      orderBy="createdBy.firstName"
                      thenBy="createdBy.lastName"
                      apiBody={listApi.body}
                      onSort={handleSort}
                    />
                  </th>
                  <th>
                    {lang.createdAt}
                    <SortButton orderBy="createdAtUtc" apiBody={listApi.body} onSort={handleSort} />
                  </th>
                  <th>
                    {lang.expiryDate}
                    <SortButton orderBy="expiryDate" apiBody={listApi.body} onSort={handleSort} />
                  </th>
                  <th data-actions>{lang.actions}</th>
                </tr>
              </thead>
              <tbody>
                <DataMap
                  data={listApi.payload?.data}
                  render={({ data: contract }) => {
                    const currentContract = selectedContracts?.find((it) => it.id === contract.id);
                    return (
                      <React.Fragment key={contract.id}>
                        <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                          <td>
                            <Button
                              class={{ button: ThemeButtonCircle }}
                              onClick={() => {
                                if (currentContract)
                                  setSelectedContracts((prevContracts) =>
                                    prevContracts?.filter((it) => it.id !== contract.id)
                                  );
                                else setSelectedContracts((prevContracts) => [...(prevContracts || []), contract]);
                              }}
                              disabled={!isContractEligibleForSelection(contract)}
                              media={currentContract ? 'fas-check-square' : 'far-square'}
                              isLoading={isLoadingSelectAll}
                              htmlElementProps={{
                                title: contract?.statementId
                                  ? `${lang.statement} ${lang.alreadyExists}`
                                  : currentContract
                                    ? lang.unselect
                                    : lang.select,
                              }}
                            />
                          </td>
                          <td>
                            <b>{contract.appNumberWithRevision}</b>
                          </td>
                          <td>
                            <ContractStatus statusCode={contract.statusCode} />
                            <br />
                            <span>
                              {contract?.isPayed && (
                                <Box
                                  class={ThemeTagRounded}
                                  style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                                >
                                  <Icon class="fas-check" />
                                  &nbsp; {lang.paid}
                                </Box>
                              )}
                              {contract?.isContractTransferred && !contract?.isTransferInvisible && (
                                <Box
                                  class={ThemeTagRounded}
                                  style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                                >
                                  <Icon class="fas-check" />
                                  &nbsp; {lang.transferred}
                                </Box>
                              )}
                              {(contract?.isSignedManually ||
                                contract?.isSignedWithDocuSing ||
                                contract?.isSignedWithSignFlow ||
                                contract?.isSentForSigning) && (
                                <Box
                                  class={ThemeTagRounded}
                                  style={{
                                    color:
                                      contract?.isSignedManually ||
                                      contract?.isSignedWithDocuSing ||
                                      contract?.isSignedWithSignFlow
                                        ? Theme.Color.success
                                        : Theme.Color.warning,
                                    backgroundColor:
                                      contract?.isSignedManually ||
                                      contract?.isSignedWithDocuSing ||
                                      contract?.isSignedWithSignFlow
                                        ? Theme.Color.successLight
                                        : Theme.Color.warningLight,
                                  }}
                                >
                                  <Icon class="fas-check" />
                                  &nbsp;
                                  {contract?.isSignedManually
                                    ? lang.signedManually
                                    : contract?.isSignedWithDocuSing
                                      ? lang.signedWithDocuSign
                                      : contract?.isSignedWithSignFlow
                                        ? lang.signedWithSignFlow
                                        : lang.sentForSigning}
                                </Box>
                              )}
                              {!contract?.client?.vertaforeCustomerNumber ? (
                                contract?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_CLIENT ? (
                                  <Box
                                    class={ThemeTagRounded}
                                    style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                                  >
                                    <Icon class="fas-ban" />
                                    &nbsp;
                                    {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, {
                                      status: lang.failed,
                                    })}
                                  </Box>
                                ) : (
                                  contract?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY && (
                                    <Box
                                      class={ThemeTagRounded}
                                      style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                                    >
                                      <Icon class="fas-ban" />
                                      &nbsp;
                                      {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, {
                                        status: lang.creatingPolicy,
                                      })}
                                    </Box>
                                  )
                                )
                              ) : contract?.vertaforeStatus === VertaforeStatuses.ERROR_UPLOAD_DOCUMENT ? (
                                <Box
                                  class={ThemeTagRounded}
                                  style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                                >
                                  <Icon class="fas-ban" />
                                  &nbsp;
                                  {replaceStringTemplateWithValues(lang.syncedWithVertaforeStatus, {
                                    status: lang.documentUploadFailed,
                                  })}
                                </Box>
                              ) : contract?.vertaforeStatus === VertaforeStatuses.NOT_UPLOADED ? (
                                <Box
                                  class={ThemeTagRounded}
                                  style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                                >
                                  <Icon class="fas-ban" />
                                  &nbsp;
                                  {replaceStringTemplateWithValues(lang.syncedWithVertaforeStatus, {
                                    status: lang.documentNotUploaded,
                                  })}
                                </Box>
                              ) : contract?.vertaforeStatus === VertaforeStatuses.UNSIGNED_UPLOADED ||
                                contract?.vertaforeStatus === VertaforeStatuses.SIGNED_UPLOADED ? (
                                <Box
                                  class={ThemeTagRounded}
                                  style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                                >
                                  <Icon class="fas-check" />
                                  &nbsp; {lang.syncedWithVertafore}
                                </Box>
                              ) : contract?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY ? (
                                <Box
                                  class={ThemeTagRounded}
                                  style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                                >
                                  <Icon class="fas-ban" />
                                  &nbsp;
                                  {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, {
                                    status: lang.creatingPolicy,
                                  })}
                                </Box>
                              ) : null}
                            </span>
                          </td>
                          <td>{contract.product?.name || lang.unknownProduct}</td>
                          <td>
                            <span>
                              {contract.vehicle.makeCode} {contract.vehicle.model}
                            </span>
                            <span> {contract.vehicle.year?.toString()}</span>
                            <br />
                            <span>{contract.vehicle?.vin}</span>
                          </td>
                          <td>{contract.client?.name}</td>
                          <td>{contract.dealer?.name}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: Theme.Size.XS }}>
                              <div>{contract.createdBy?.fullName || lang.unknownUser}</div>
                              <div>
                                <DropdownButton
                                  buttonMedia={<Icon class="fas-user" />}
                                  horizontalPosition="auto_fixed"
                                  verticalPosition="auto_fixed"
                                  subcomponents={ThemeDropdownButtonPopup}
                                  htmlElementProps={{ title: lang.changeOwner }}
                                  title={lang.changeContractCreator}
                                  disabled={!permissions.CHANGE_CONTRACT_CREATOR}
                                  tabIndex={null}
                                  dropdownOptions={
                                    <DataMap
                                      data={sortBy(
                                        userListApi?.payload?.data?.filter(
                                          (user) =>
                                            user?.roles?.some((r) => r.id === Roles.administrator) ||
                                            user?.dealers?.some((d) => d.id === contract?.dealerId)
                                        ),
                                        (it) => it.fullName
                                      )}
                                      render={({ data: user }) => (
                                        <Button
                                          key={user.id}
                                          onClick={setNewCreator}
                                          data={{ contract, user }}
                                          render={user.displayInfo.title}
                                          class={ThemeButtonMenuItem}
                                        />
                                      )}
                                    />
                                  }
                                />
                              </div>
                            </div>
                          </td>
                          <td>{localizeDate(contract?.createdAtLocal, defaultDateTimeFormat)}</td>
                          <td>{localizeDate(contract?.expiryDate, defaultDateFormat)}</td>
                          <td>
                            <Box class={ThemeWrapperStack}>
                              <ContractStatusStateMachine
                                statusInfo={statusInfo}
                                contractId={contract.id}
                                active
                                currentStatus={contract.statusCode ?? ContractStatusCode.OPEN_SUBMITTED}
                                onChange={() => listApi.execute((b) => b)}
                              />
                              <Button
                                class={ThemeButtonCircle}
                                media={<Icon class="fas-info-circle" />}
                                onClick={pageRouter.updateParams}
                                data={{ id: contract.id.toString(), action: SalesContractAction.INFO }}
                                htmlElementProps={{ title: lang.info }}
                                active
                              />
                              <LinkButton
                                class={ThemeButtonCircle}
                                url={contract.documentationAggregate?.url}
                                media={<Icon class="fas-download" />}
                                target="_blank"
                                disabled={!contract?.documentationAggregate?.url}
                                active
                                htmlElementProps={{
                                  title: !contract?.documentationAggregate?.url
                                    ? lang.missingContractDocument
                                    : lang.download,
                                }}
                              />
                              <Button
                                class={ThemeButtonCircle}
                                media={<Icon class="fas-edit" />}
                                onClick={redirectToNewContract}
                                disabled={
                                  ContractStatusCode.OPEN_SUBMITTED !== contract?.statusCode ||
                                  !permissions.EDIT_CONTRACT
                                }
                                active
                                data={{ action: SalesNewContractAction.VEHICLE, id: contract.id.toString() }}
                                htmlElementProps={{ title: lang.edit }}
                              />
                            </Box>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  }}
                />
              </tbody>
            </table>
          </>
        }
        footer={
          <Pagination
            class={ThemePaginationRounded}
            count={listApi.payload?.count}
            offset={listApi.body?.offset}
            limit={listApi.body?.limit}
            onLimitChange={(limit) =>
              pageRouter.updateQuery(
                merge(pageRouter.query, {
                  list: {
                    limit,
                    offset: 0,
                  },
                })
              )
            }
            onOffsetChange={(offset) =>
              pageRouter.updateQuery(
                merge(pageRouter.query, {
                  list: {
                    offset,
                  },
                })
              )
            }
          />
        }
      />
    </>
  );
}
